import {
  useState, useEffect 
} from 'react';
import {
  useNavigate, Link, useSearchParams 
} from 'react-router-dom';
import {
  Utensils, ArrowLeft, Mail, Check, X 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  requestPasswordReset, confirmPasswordReset 
} from '@/lib/cognito-client';

const PASSWORD_REQUIREMENTS = [
  {
    id: 'length',
    label: 'At least 12 characters',
    test: (p: string) => p.length >= 12 
  },
  {
    id: 'lowercase',
    label: 'One lowercase letter',
    test: (p: string) => /[a-z]/.test(p) 
  },
  {
    id: 'uppercase',
    label: 'One uppercase letter',
    test: (p: string) => /[A-Z]/.test(p) 
  },
  {
    id: 'number',
    label: 'One number',
    test: (p: string) => /\d/.test(p) 
  },
  {
    id: 'symbol',
    label: 'One special character',
    test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p) 
  },
];

type Step = 'request' | 'confirm' | 'success';

function getRequestResetErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    if (err.message.includes('User does not exist')) return '';
    return err.message;
  }
  return 'Failed to send reset code';
}

function getResendCodeErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    if (err.message.includes('Attempt limit exceeded')) {
      return 'Too many attempts. Please try again later.';
    }
    return 'Failed to resend code. Please try again.';
  }
  return 'Failed to resend code';
}

function getConfirmResetErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    if (err.message.includes('Invalid verification code')) {
      return 'Invalid or expired code. Please try again.';
    }
    if (err.message.includes('Attempt limit exceeded')) {
      return 'Too many attempts. Please try again later.';
    }
    return err.message;
  }
  return 'Failed to reset password';
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email') ?? '';
  const isResetRequired = searchParams.get('reset') === 'true';

  const [step, setStep] = useState<Step>(isResetRequired && emailFromUrl ? 'confirm' : 'request');
  const [email, setEmail] = useState(emailFromUrl);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(
    isResetRequired ? 'A password reset code has been sent to your email.' : null
  );

  useEffect(() => {
    if (infoMessage) {
      const timer = setTimeout(() => setInfoMessage(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [infoMessage]);

  const requirementsMet = PASSWORD_REQUIREMENTS.map((req) => ({
    ...req,
    met: req.test(newPassword) 
  }));
  const allRequirementsMet = requirementsMet.every((r) => r.met);
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;

  const executeRequestReset = async () => {
    try {
      await requestPasswordReset(email);
      setStep('confirm');
    } catch (err) {
      const errorMsg = getRequestResetErrorMessage(err);
      if (errorMsg) setError(errorMsg);
      else setStep('confirm');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestReset = (e: React.BaseSyntheticEvent) => {
    e.preventDefault();
    setError(null);
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setIsSubmitting(true);
    void executeRequestReset();
  };

  const executeResendCode = async () => {
    try {
      await requestPasswordReset(email);
      setInfoMessage('A new code has been sent to your email.');
      setCode('');
    } catch (err) {
      setError(getResendCodeErrorMessage(err));
    } finally {
      setIsResending(false);
    }
  };

  const handleResendCode = () => {
    setError(null);
    setIsResending(true);
    void executeResendCode();
  };

  const executeConfirmReset = async () => {
    try {
      await confirmPasswordReset(email, code, newPassword);
      setStep('success');
    } catch (err) {
      setError(getConfirmResetErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReset = (e: React.BaseSyntheticEvent) => {
    e.preventDefault();
    setError(null);
    if (!code || !newPassword) {
      setError('Please enter the code and new password');
      return;
    }
    if (!allRequirementsMet) {
      setError('Password does not meet requirements');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }
    setIsSubmitting(true);
    void executeConfirmReset();
  };

  if (step === 'success') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Password Reset Complete</CardTitle>
            <CardDescription>
              Your password has been successfully reset. You can now sign in with your new password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/login')} className="w-full">Sign In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary">
            <Utensils className="h-6 w-6 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl">
            {step === 'request' ? 'Forgot Password' : 'Reset Password'}
          </CardTitle>
          <CardDescription>
            {step === 'request'
              ? "Enter your email and we'll send you a reset code"
              : 'Enter the code from your email and create a new password'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'request' ? (
            <RequestForm
              email={email}
              setEmail={setEmail}
              error={error}
              isSubmitting={isSubmitting}
              onSubmit={handleRequestReset}
            />
          ) : (
            <ConfirmForm
              email={email}
              code={code}
              setCode={setCode}
              newPassword={newPassword}
              setNewPassword={setNewPassword}
              confirmPassword={confirmPassword}
              setConfirmPassword={setConfirmPassword}
              error={error}
              infoMessage={infoMessage}
              isSubmitting={isSubmitting}
              isResending={isResending}
              requirementsMet={requirementsMet}
              allRequirementsMet={allRequirementsMet}
              passwordsMatch={passwordsMatch}
              onSubmit={handleConfirmReset}
              onResend={handleResendCode}
              onBack={() => setStep('request')}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface RequestFormProps {
  readonly email: string;
  readonly setEmail: (email: string) => void;
  readonly error: string | null;
  readonly isSubmitting: boolean;
  readonly onSubmit: (e: React.BaseSyntheticEvent) => void;
}

function RequestForm({
  email, setEmail, error, isSubmitting, onSubmit 
}: RequestFormProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium leading-none">Email</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm"
          />
        </div>
      </div>
      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? 'Sending...' : 'Send Reset Code'}
      </Button>
      <Link to="/login" className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />Back to sign in
      </Link>
    </form>
  );
}

interface ConfirmFormProps {
  readonly email: string;
  readonly code: string;
  readonly setCode: (code: string) => void;
  readonly newPassword: string;
  readonly setNewPassword: (password: string) => void;
  readonly confirmPassword: string;
  readonly setConfirmPassword: (password: string) => void;
  readonly error: string | null;
  readonly infoMessage: string | null;
  readonly isSubmitting: boolean;
  readonly isResending: boolean;
  readonly requirementsMet: Array<{
    id: string;
    label: string;
    met: boolean 
  }>;
  readonly allRequirementsMet: boolean;
  readonly passwordsMatch: boolean;
  readonly onSubmit: (e: React.BaseSyntheticEvent) => void;
  readonly onResend: () => void;
  readonly onBack: () => void;
}

function ConfirmForm(props: ConfirmFormProps) {
  const {
    email, code, setCode, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    error, infoMessage, isSubmitting, isResending, requirementsMet, allRequirementsMet,
    passwordsMatch, onSubmit, onResend, onBack,
  } = props;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {infoMessage && <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-700">{infoMessage}</div>}
      {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
      <div className="rounded-md bg-muted p-3 text-sm">
        <span className="text-muted-foreground">Reset code sent to: </span>
        <span className="font-medium">{email}</span>
      </div>
      <div className="space-y-2">
        <label htmlFor="code" className="text-sm font-medium leading-none">Reset Code</label>
        <input
          id="code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Enter 6-digit code"
          autoComplete="one-time-code"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-center tracking-widest font-mono"
          maxLength={6}
        />
        <button type="button" onClick={onResend} disabled={isResending} className="text-sm text-primary hover:underline disabled:opacity-50">
          {isResending ? 'Sending...' : "Didn't receive a code? Resend"}
        </button>
      </div>
      <div className="space-y-2">
        <label htmlFor="newPassword" className="text-sm font-medium leading-none">New Password</label>
        <input
          id="newPassword"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Enter new password"
          autoComplete="new-password"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
      </div>
      <div className="space-y-2 rounded-md bg-muted/50 p-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Password Requirements</p>
        <ul className="space-y-1">
          {requirementsMet.map((req) => (
            <li key={req.id} className="flex items-center gap-2 text-sm">
              {req.met ? <Check className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-muted-foreground" />}
              <span className={req.met ? 'text-green-600' : 'text-muted-foreground'}>{req.label}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="space-y-2">
        <label htmlFor="confirmPassword" className="text-sm font-medium leading-none">Confirm Password</label>
        <input
          id="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm new password"
          autoComplete="new-password"
          className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ${confirmPassword && !passwordsMatch ? 'border-destructive' : 'border-input'}`}
        />
        {confirmPassword && !passwordsMatch && <p className="text-xs text-destructive">Passwords do not match</p>}
      </div>
      <Button type="submit" disabled={isSubmitting || !allRequirementsMet || !passwordsMatch} className="w-full">
        {isSubmitting ? 'Resetting...' : 'Reset Password'}
      </Button>
      <button type="button" onClick={onBack} className="flex items-center justify-center gap-2 w-full text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />Use different email
      </button>
    </form>
  );
}
